import { useEffect, useSyncExternalStore } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useAuthSession } from '@/api';
import { activePlayerRewards } from '@/domain/rewards';
import { cn } from '@/lib/cn';
import { Icon } from '@/ui/common/icon';
import { Toast } from '@/ui/common/toast';
import {
  clearRewardToast,
  getRewardToast,
  getRewardToastVersion,
  subscribeRewardToast,
} from '@/player/reward-notifications';

export interface RewardsNotificationBannerProps {
  className?: string;
  /** When true, hide on xl+ (desktop rail hosts its own copy). */
  mobileOnly?: boolean;
  /** When true, hide below xl (shown in the desktop betslip rail). */
  desktopOnly?: boolean;
}

/**
 * Legacy `Home.RewardsNotification` — count banner linking to profile bonuses,
 * plus a transient toast when a Reward socket assigns a new reward.
 */
export function RewardsNotificationBanner({
  className,
  mobileOnly = false,
  desktopOnly = false,
}: RewardsNotificationBannerProps) {
  const { t } = useTranslation();
  const { loggedIn, user } = useAuthSession();
  const toastVersion = useSyncExternalStore(
    subscribeRewardToast,
    getRewardToastVersion,
    getRewardToastVersion,
  );
  const toast = toastVersion > 0 ? getRewardToast() : null;

  const count = loggedIn ? activePlayerRewards(user?.Rewards).length : 0;

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => clearRewardToast(), 4000);
    return () => window.clearTimeout(timer);
  }, [toast?.id, toast?.at]);

  if (!loggedIn) return null;
  if (!toast && count === 0) return null;

  const visibility = cn(
    mobileOnly && 'xl:hidden',
    desktopOnly && 'hidden xl:block',
  );

  return (
    <div className={cn('flex w-full flex-col gap-2', visibility, className)}>
      {toast ? (
        <Toast tone="success" onDismiss={clearRewardToast}>
          {t('NewReward', { defaultValue: 'You have a new reward!' })}
        </Toast>
      ) : null}
      {count > 0 ? (
        <Link
          to="/profile/bonuses"
          className={cn(
            'flex w-full items-center gap-3 rounded-lg border-[1.5px] border-action-primary',
            'bg-bg-surface px-3 py-2.5 text-left transition-opacity hover:opacity-90',
          )}
        >
          <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-action-primary text-text-on-action">
            <Icon name="bonus-gift" size={18} className="text-text-on-action" />
          </div>
          <p className="min-w-0 flex-1 text-body-m font-bold text-text-primary">
            {t('YouHaveNRewards', {
              defaultValue: 'You have {{count}} Rewards!',
              count,
            })}
          </p>
          <Icon name="arrow-right" size={16} className="shrink-0 text-icon-muted" />
        </Link>
      ) : null}
    </div>
  );
}
